import { useCallback, useMemo, useRef, useState } from 'react';
import { useToast } from '@/contexts/ToastContext';
import {
  useCreateBulkPayOrderDeferred,
  useCompleteBulkPayment,
  useMintIntent,
  useSettleWithCredit,
  type CreditOption,
  type CreditApplicationInput,
} from '@/api/extensions/useRegistrationOps';
import { StripeCheckoutDialog } from './StripeCheckoutDialog';

interface BulkPayDialogProps {
  open: boolean;
  onClose: () => void;
  /** Registrations to settle in one Stripe charge. */
  registrationIds: string[];
  /**
   * Optional unsettled `account_adjustments` ids to roll into the same
   * capture so manual ledger debt clears alongside event-registration
   * debt. The Funds-tab "Pay outstanding balance" passes these when the
   * user has manual charges outstanding. Server validates ownership +
   * unsettled state, silently skips ids that don't qualify.
   */
  manualChargeAdjustmentIds?: string[];
  /** Optional pre-known total for the dialog header before the quote arrives. */
  fallbackTotal?: number | null;
  /**
   * Accepted for backwards compatibility with callers from the eager-mint
   * era. Ignored — the deferred-mint dialog reads fees from the quote's
   * totals_by_method and flips between card/ACH client-side.
   */
  transactionFeeTotal?: number | null;
  onSuccess?: (result: {
    confirmed: string[];
    failed: string[];
    capturedAmount: number;
  }) => void;
}

/**
 * Single-shot bulk-pay dialog. Uses the deferred-mint flow: a local
 * payment row is created via /pay-now-bulk-deferred but no Stripe PI
 * is minted until the user clicks Pay. Verifies via /complete-bulk
 * once Stripe Elements confirms.
 *
 * Distinct from PayNowDialog because the bulk capture has different
 * error semantics: a single Stripe charge can result in partial
 * per-registration confirmation failures that need to surface as a
 * "partial success" warning instead of a plain success toast.
 */
export function BulkPayDialog({
  open,
  onClose,
  registrationIds,
  manualChargeAdjustmentIds,
  fallbackTotal,
  onSuccess,
}: BulkPayDialogProps) {
  const { showSuccess, showWarning } = useToast();
  const createOrderDeferred = useCreateBulkPayOrderDeferred();
  const completeBulk = useCompleteBulkPayment();
  const mintIntent = useMintIntent();
  const settleWithCredit = useSettleWithCredit();
  // Per-org credit affordances for this basket (split tender). A bulk basket
  // can span multiple orgs, and org credit is org-scoped — so each org's
  // applicable amount is its portion of THIS basket, not its raw balance. The
  // backend computes these (in lock-step with the authoritative settlement
  // caps) and returns `credit_options` on the quote; the checkout dialog turns
  // them into the apply-credit control (dropdown + badges when multi-org). This
  // is what prevents org-A credit from paying org-B's fees and keeps the Stripe
  // Elements amount equal to the minted PaymentIntent amount.
  const [creditOptions, setCreditOptions] = useState<CreditOption[]>([]);
  // payment_id from the active quote — needed for the no-Stripe credit-only
  // settlement path.
  const [paymentId, setPaymentId] = useState<string | null>(null);

  // Snapshot the ids the order was quoted for so the eventual capture
  // always fires against the original batch — otherwise a registration
  // landing mid-flight could silently be added to / removed from the order.
  const idsSnapshotRef = useRef<string[]>([]);
  const manualIdsSnapshotRef = useRef<string[]>([]);
  const triggerKey = useMemo(
    () =>
      JSON.stringify({
        regs: registrationIds,
        manual: manualChargeAdjustmentIds ?? [],
      }),
    [registrationIds, manualChargeAdjustmentIds],
  );

  const quoteOrder = useCallback(async () => {
    idsSnapshotRef.current = [...registrationIds];
    manualIdsSnapshotRef.current = [...(manualChargeAdjustmentIds ?? [])];
    // Reset the credit affordances until the fresh quote tells us what applies
    // to this basket.
    setCreditOptions([]);
    setPaymentId(null);
    const res = await createOrderDeferred.mutateAsync({
      registration_ids: idsSnapshotRef.current,
      manual_charge_adjustment_ids:
        manualIdsSnapshotRef.current.length > 0
          ? manualIdsSnapshotRef.current
          : undefined,
    });
    setCreditOptions(res.credit_options ?? []);
    setPaymentId(res.payment_id ?? null);
    return res;
  }, [createOrderDeferred, registrationIds, manualChargeAdjustmentIds]);

  const confirmIntent = useCallback(
    async (
      paymentId: string,
      paymentMethodType: 'card' | 'us_bank_account',
      creditAmount?: number,
      creditApplications?: CreditApplicationInput[],
    ) => {
      return await mintIntent.mutateAsync({
        payment_id: paymentId,
        payment_method_type: paymentMethodType,
        credit_amount: creditAmount,
        credit_applications: creditApplications,
      });
    },
    [mintIntent],
  );

  // Zero-charge: the user's credit covers the whole basket — settle with no
  // Stripe charge. Confirms every covered registration server-side.
  const handleSettleWithCredit = useCallback(
    async (applications: CreditApplicationInput[]) => {
      if (!paymentId) throw new Error('Payment is not ready yet');
      const result = await settleWithCredit.mutateAsync({
        payment_id: paymentId,
        credit_applications: applications,
      });
      const confirmedCount = result.confirmed_registration_ids.length;
      const failedCount = result.failed_registration_ids.length;
      if (failedCount > 0) {
        showWarning(
          'Partial success',
          `${confirmedCount} of ${confirmedCount + failedCount} ` +
            `registrations confirmed with credit. Refresh and retry the others.`,
        );
      } else {
        showSuccess(
          'Paid with credit!',
          `${confirmedCount} registrations confirmed — no card charged.`,
        );
      }
      onSuccess?.({
        confirmed: result.confirmed_registration_ids,
        failed: result.failed_registration_ids,
        capturedAmount: result.credit_applied,
      });
    },
    [paymentId, settleWithCredit, onSuccess, showSuccess, showWarning],
  );

  const handleApprove = useCallback(
    async (
      paymentIntentId: string,
      methodInfo: { paymentMethodType: string | null; status: string },
    ) => {
      const result = await completeBulk.mutateAsync(paymentIntentId);
      const confirmedCount = result.confirmed_registration_ids.length;
      const failedCount = result.failed_registration_ids.length;
      const isAchProcessing =
        methodInfo.paymentMethodType === 'us_bank_account' ||
        methodInfo.status === 'processing';
      if (failedCount > 0) {
        showWarning(
          'Partial success',
          `${confirmedCount} of ${confirmedCount + failedCount} ` +
            `registrations confirmed. Refresh and retry the others.`,
        );
      } else if (isAchProcessing) {
        showSuccess(
          'Entries confirmed!',
          `${confirmedCount} registrations confirmed. Bank transfer is settling (3-5 business days).`,
        );
      } else {
        showSuccess(
          'Payment successful!',
          `${confirmedCount} registrations confirmed.`,
        );
      }
      onSuccess?.({
        confirmed: result.confirmed_registration_ids,
        failed: result.failed_registration_ids,
        capturedAmount: result.captured_amount,
      });
    },
    [completeBulk, onSuccess, showSuccess, showWarning],
  );

  const count = registrationIds.length;
  const subtitle = `Settling ${count} registration${
    count === 1 ? '' : 's'
  } in one Stripe checkout.`;

  return (
    <StripeCheckoutDialog
      open={open}
      onClose={onClose}
      title="Pay outstanding balance"
      subtitle={subtitle}
      amountLabel="Total due"
      fallbackTotal={fallbackTotal}
      triggerKey={triggerKey}
      quoteOrder={quoteOrder}
      confirmIntent={confirmIntent}
      creditOptions={creditOptions}
      onSettleWithCredit={handleSettleWithCredit}
      onApprove={handleApprove}
    />
  );
}
